/**
 * Experiment middleware for React Router v7 SSR
 *
 * Fetches all Amplitude experiment variants on the server and makes them
 * available to loaders via context. The root loader passes them to the
 * client through loader data, where HydratedExperimentProvider injects
 * them into a `<script>` tag for immediate client-side access.
 *
 * Anonymous users (no Amplitude cookie) get a newly generated device_id
 * with a Set-Cookie header, following pixai-server's getOrSetAmpDeviceId
 * pattern so they receive experiment assignments on their first visit.
 *
 * @see packages/pixai-server/src/util/amplitude.ts
 * @see packages/pixai-app/src/middleware/observability.middleware.ts
 */
import { AmplitudeCookie, Experiment } from '@amplitude/experiment-node-server'
import { BrokenCircuitError } from 'cockatiel'
import { parseCookie } from 'cookie-es'
import { createAmplitudeFetchV2Breaker } from 'pixai-server/src/util/amplitudeFetchV2Breaker'
import {
  getHydratableExperimentVariants,
  type HydratableExperimentVariants,
} from 'pixai-utils/cache/ssrExperiment'
import { createContext, type MiddlewareFunction } from 'react-router'
import logger from 'src/utils/logger.server'
import { applySsrRenderOverride } from 'src/utils/router/ssrModeOverride.server'
import { reportSsrCaughtError } from 'src/utils/ssr-caught-error.server'
import { authContext } from './auth.middleware.server'

// ============================================================================
// Experiment Context Type
// ============================================================================

export type ExperimentVariants = HydratableExperimentVariants

/**
 * React Router context for server-side experiment variants.
 * Use `context.get(experimentContext)` in loaders.
 */
export const experimentContext = createContext<ExperimentVariants>({})

// ============================================================================
// Configuration
// ============================================================================

const AMP_COOKIE_DOMAIN = '.pixai.art'

/**
 * Build a Set-Cookie string for the Amplitude device_id cookie.
 * Omits Domain on localhost (so the browser scopes it to the exact host)
 * and adds Secure on HTTPS origins.
 */
function buildAmpSetCookie(cookieValue: string, requestUrl: string): string {
  const url = new URL(requestUrl)
  const isLocalhost =
    url.hostname === 'localhost' || url.hostname === '127.0.0.1'
  const parts = [
    `${getAmpCookieName()}=${cookieValue}`,
    !isLocalhost && `Domain=${AMP_COOKIE_DOMAIN}`,
    'Path=/',
    'Max-Age=31536000',
    'SameSite=Lax',
    url.protocol === 'https:' && 'Secure',
  ]
  return parts.filter(Boolean).join('; ')
}

// ============================================================================
// Amplitude Server-side Experiment Client
// ============================================================================

// Lazy singleton: keep env-derived init out of module load so importing this
// file without VITE_AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY (tests, tooling) is safe.
let cachedExperimentClient:
  | ReturnType<typeof Experiment.initializeRemote>
  | undefined
const getExperimentClient = () =>
  (cachedExperimentClient ??= Experiment.initializeRemote(
    import.meta.env.VITE_AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY ?? '',
    {
      fetchTimeoutMillis: 800,
      fetchRetries: 1,
      fetchRetryBackoffMinMillis: 0,
      fetchRetryTimeoutMillis: 800,
    },
  ))

// One breaker per SSR process; policy shared with pixai-server.
const fetchV2Breaker = createAmplitudeFetchV2Breaker()
fetchV2Breaker.onBreak(() =>
  logger.warn('Amplitude fetchV2 circuit opened (cockatiel)'),
)
fetchV2Breaker.onReset(() =>
  logger.info('Amplitude fetchV2 circuit reset (cockatiel)'),
)

/** Matches pixai-server: browser SDK 2.0 cookie format */
const BROWSER_SDK_2 = true

// Lazy singleton: same rationale as getExperimentClient above.
let cachedAmpCookieName: string | undefined
const getAmpCookieName = () =>
  (cachedAmpCookieName ??= AmplitudeCookie.cookieName(
    import.meta.env.VITE_AMPLITUDE_API_KEY ?? '',
    BROWSER_SDK_2,
  ))

interface AmpCookieIdentity {
  deviceId: string | undefined
  /** userId from the Amplitude browser SDK cookie (fallback for environments
   *  where the http-only user_token JWT is unavailable, e.g. localhost). */
  ampUserId: string | undefined
}

/**
 * Read the Amplitude device_id and userId from the Cookie header.
 * Mirrors pixai-server's amplitudeUtilities.getAmpDeviceId.
 */
function parseAmpCookie(cookieHeader: string): AmpCookieIdentity {
  const raw = parseCookie(cookieHeader)[getAmpCookieName()]
  if (!raw) return { deviceId: undefined, ampUserId: undefined }

  try {
    const parsed = AmplitudeCookie.parse(decodeURIComponent(raw), BROWSER_SDK_2)
    return {
      deviceId: parsed.device_id,
      ampUserId:
        typeof parsed.user_id === 'string' && parsed.user_id
          ? parsed.user_id
          : undefined,
    }
  } catch {
    // expected-error: AmplitudeCookie.parse throws on malformed/foreign
    // cookies, which is routine for first-time visitors or browsers that
    // sent an unrelated cookie. Treat as "no usable amp identity".
    return { deviceId: undefined, ampUserId: undefined }
  }
}

/**
 * Fetch all experiment variants for a device. Throws on failure so the
 * SWR cache layer can leave the previous cached entry untouched —
 * `getHydratableExperimentVariants` will fall open with `{}` if the
 * cache miss path also can't reach Amplitude.
 */
async function fetchAllVariants(
  deviceId: string,
  userId: string | undefined,
): Promise<ExperimentVariants> {
  try {
    const user = userId
      ? { device_id: deviceId, user_id: userId }
      : { device_id: deviceId }
    const raw = await fetchV2Breaker.execute(() =>
      getExperimentClient().fetchV2(user),
    )
    const result: ExperimentVariants = {}
    for (const [key, variant] of Object.entries(raw)) {
      if (typeof variant.value === 'string' && variant.value !== '') {
        result[key] = variant.value
      }
    }
    return result
  } catch (err) {
    if (err instanceof BrokenCircuitError) {
      logger.warn('fetchAllVariants short-circuited by cockatiel breaker', {
        deviceId,
        userId,
      })
    } else {
      logger.warn('fetchAllVariants failed', {
        error: err instanceof Error ? err.message : String(err),
        deviceId,
        userId,
      })
    }
    throw err
  }
}

// ============================================================================
// Middleware
// ============================================================================

/**
 * Experiment middleware that fetches Amplitude variants and sets them
 * on the React Router context.
 *
 * For anonymous users (no Amplitude cookie), generates a new device_id
 * and sets the cookie on the response so the same identity persists
 * across subsequent requests.
 *
 * Usage in root route:
 *   export const middleware = [experimentMiddleware, ...]
 *
 * Then in loaders:
 *   export function loader({ context }) {
 *     const experiments = context.get(experimentContext)
 *     return { experiments }
 *   }
 */
export const experimentMiddleware: MiddlewareFunction = async (
  { request, context },
  next,
) => {
  const cookieHeader = request.headers.get('cookie') ?? ''
  const { deviceId: ampDeviceId, ampUserId } = parseAmpCookie(cookieHeader)
  let deviceId = ampDeviceId
  let setCookieHeader: string | undefined

  // Anonymous user: generate device_id and prepare Set-Cookie.
  // Mirrors pixai-server's getOrSetAmpDeviceId pattern.
  if (!deviceId) {
    deviceId = crypto.randomUUID()
    const cookieValue = AmplitudeCookie.generate(deviceId, BROWSER_SDK_2)
    setCookieHeader = buildAmpSetCookie(cookieValue, request.url)
  }

  // Prefer JWT-verified userId; fall back to Amplitude cookie userId
  // (useful on localhost where http-only user_token cookie is unavailable).
  const { userId: jwtUserId } = context.get(authContext)
  const userId = jwtUserId || ampUserId
  const cachedVariants = await getHydratableExperimentVariants({
    userId,
    deviceId,
    fetchVariants: () => fetchAllVariants(deviceId, userId),
    onError: (event, err) => {
      logger.warn(`SSR experiment ${event} failed`, {
        message: err instanceof Error ? err.message : String(err),
      })
      reportSsrCaughtError(err, {
        origin: 'experiment-middleware',
        operation: event,
      })
    },
  })

  // `applySsrRenderOverride` returns a fresh `variants` object with
  // the SSR dual-mode flag patched in when an override is in effect,
  // leaving the original SWR-cached value untouched (shared across
  // requests for the same identity). When the override is gated off
  // in this environment, the call is a no-op aside from a shallow
  // clone of `cachedVariants`.
  const { variants, override: renderOverride } = applySsrRenderOverride(
    cachedVariants,
    request,
  )
  if (renderOverride) {
    logger.info('SSR render mode override applied', {
      flag: 'pixai-app-full-ssr',
      value: renderOverride.value,
      source: renderOverride.source,
      rawValue: renderOverride.rawValue,
      url: request.url,
    })
  }

  context.set(experimentContext, variants)

  const response = (await next()) as Response

  if (setCookieHeader) {
    response.headers.append('Set-Cookie', setCookieHeader)
  }

  return response
}
